import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui';
import type { RiverRushOtter } from '../types';

interface OtterSelectorProps {
  isOpen: boolean;
  otters: RiverRushOtter[];
  selectedOtterId: string;
  onSelect: (otterId: string) => void;
  onClose: () => void;
}

const otterColors: Record<string, string> = {
  'sea-otter': 'from-amber-600 to-amber-800',
  'giant-otter': 'from-stone-600 to-stone-800',
  'eurasian-otter': 'from-amber-700 to-amber-900',
  'asian-small-clawed-otter': 'from-gray-500 to-gray-700',
  'smooth-coated-otter': 'from-amber-500 to-amber-700',
  'north-american-river-otter': 'from-amber-600 to-amber-800',
};

export function OtterSelector({
  isOpen,
  otters,
  selectedOtterId,
  onSelect,
  onClose,
}: OtterSelectorProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 20 }}
            className="bg-gradient-to-b from-otter-700 to-otter-900 rounded-3xl p-4 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Choose Your Otter</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Otter Grid */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {otters.map((otter) => {
                const isSelected = otter.id === selectedOtterId;
                const gradientClass = otterColors[otter.speciesId] || 'from-amber-600 to-amber-800';

                return (
                  <motion.button
                    key={otter.id}
                    onClick={() => otter.unlocked && onSelect(otter.id)}
                    disabled={!otter.unlocked}
                    whileTap={otter.unlocked ? { scale: 0.98 } : {}}
                    className={`
                      w-full p-4 rounded-xl text-left transition-all
                      ${otter.unlocked
                        ? `bg-gradient-to-r ${gradientClass} hover:shadow-lg`
                        : 'bg-gray-800 opacity-60'
                      }
                      ${isSelected ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-otter-800' : ''}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      {/* Otter icon/preview */}
                      <div className={`
                        w-14 h-14 rounded-full flex items-center justify-center
                        ${otter.unlocked ? 'bg-white/20' : 'bg-black/30'}
                      `}>
                        {otter.unlocked ? (
                          <span className="text-2xl">🦦</span>
                        ) : (
                          <Lock className="w-6 h-6 text-gray-500" />
                        )}
                      </div>

                      {/* Otter info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-bold ${otter.unlocked ? 'text-white' : 'text-gray-400'}`}>
                            {otter.name}
                          </h3>
                          {isSelected && (
                            <span className="bg-yellow-500 p-0.5 rounded-full">
                              <Check className="w-3 h-3 text-yellow-900" />
                            </span>
                          )}
                        </div>

                        {otter.unlocked ? (
                          <>
                            <p className="text-sm text-white/70 mt-0.5">
                              <Sparkles className="w-3 h-3 inline mr-1" />
                              {otter.ability}
                            </p>
                            <p className="text-xs text-white/50 mt-1">
                              {otter.abilityDescription}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500 mt-1">
                            {otter.unlockCondition}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Confirm button */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <Button
                onClick={onClose}
                className="w-full"
                variant="primary"
              >
                Confirm Selection
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
